import React, { useState, useEffect } from 'react';
import { Building2, Plus, Edit, Trash2, AlertTriangle, CheckCircle, Search, Loader2, HardHat, Sprout } from 'lucide-react';
import { supabase } from '../config/supabase';
import { consultarCNPJ, obterGrauRiscoPorCNAE, formatarEndereco, calcularDimensionamentoCIPA, formatarCNAE, DadosEmpresa } from '../services/cnpjService';
import { useParametrosPerfil } from '../hooks/useParametrosPerfil';
import { validarPermissaoCadastroEmpresa } from '../services/empresaValidationService';
import { useNotification } from '../hooks/useNotification';
import NotificationModal from './NotificationModal';
import { isMiningCompany, getComissaoVisualInfo, getRiscosEspecificos, atualizarTipoComissao } from '../utils/cipaminUtils';

interface Empresa {
  id: string;
  nome: string;
  cnpj: string;
  razao_social: string;
  status: string;
  numero_funcionarios: number;
  grau_risco: number;
  cnae_principal?: string;
  cnae_descricao?: string;
  tipo_comissao?: string;
  created_at: string;
}

interface ParametroPerfil {
  id: string;
  tipo_usuario: string;
  max_empresas: number;
  max_eleitores: number;
  max_funcionarios: number;
  descricao: string;
  ativo: boolean;
}

interface EmpresasManagerProps {
  usuarioId: string;
  tipoUsuario: string;
  onEmpresaChange?: () => void;
}

const EmpresasManager: React.FC<EmpresasManagerProps> = ({ usuarioId, tipoUsuario, onEmpresaChange }) => {
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEmpresa, setEditingEmpresa] = useState<Empresa | null>(null);
  const [consultandoCNPJ, setConsultandoCNPJ] = useState(false);
  
  // Hook para parâmetros de perfil
  const { parametros, podeTerFuncionarios, getLimiteFuncionarios } = useParametrosPerfil(tipoUsuario);
  
  // Hook para notificações
  const { notification, hideNotification, showError, showSuccess } = useNotification();

  // Funções de máscara
  const aplicarMascaraCNPJ = (valor: string) => {
    // Remove tudo que não é dígito
    const apenasNumeros = valor.replace(/\D/g, '');
    
    // Aplica a máscara do CNPJ: 00.000.000/0000-00
    if (apenasNumeros.length <= 2) {
      return apenasNumeros;
    } else if (apenasNumeros.length <= 5) {
      return `${apenasNumeros.slice(0, 2)}.${apenasNumeros.slice(2)}`;
    } else if (apenasNumeros.length <= 8) {
      return `${apenasNumeros.slice(0, 2)}.${apenasNumeros.slice(2, 5)}.${apenasNumeros.slice(5)}`;
    } else if (apenasNumeros.length <= 12) {
      return `${apenasNumeros.slice(0, 2)}.${apenasNumeros.slice(2, 5)}.${apenasNumeros.slice(5, 8)}/${apenasNumeros.slice(8)}`;
    } else {
      return `${apenasNumeros.slice(0, 2)}.${apenasNumeros.slice(2, 5)}.${apenasNumeros.slice(5, 8)}/${apenasNumeros.slice(8, 12)}-${apenasNumeros.slice(12, 14)}`;
    }
  };

  const aplicarMascaraTelefone = (valor: string) => {
    // Remove tudo que não é dígito
    const apenasNumeros = valor.replace(/\D/g, '');
    
    // Aplica a máscara do telefone: (00) 0000-0000 ou (00) 00000-0000
    if (apenasNumeros.length <= 2) {
      return apenasNumeros;
    } else if (apenasNumeros.length <= 6) {
      return `(${apenasNumeros.slice(0, 2)}) ${apenasNumeros.slice(2)}`;
    } else if (apenasNumeros.length <= 10) {
      return `(${apenasNumeros.slice(0, 2)}) ${apenasNumeros.slice(2, 6)}-${apenasNumeros.slice(6)}`;
    } else {
      return `(${apenasNumeros.slice(0, 2)}) ${apenasNumeros.slice(2, 7)}-${apenasNumeros.slice(7, 11)}`;
    }
  };

  const validarEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Função para consultar CNPJ automaticamente
  const consultarCNPJAutomatico = async (cnpj: string) => {
    const cnpjLimpo = cnpj.replace(/\D/g, '');
    
    // Só consulta se o CNPJ estiver completo (14 dígitos)
    if (cnpjLimpo.length === 14) {
      setConsultandoCNPJ(true);
      
      try {
        const dadosEmpresa = await consultarCNPJ(cnpj);
        
        if (dadosEmpresa) {
          // Obtém o grau de risco do CNAE
          const grauRisco = await obterGrauRiscoPorCNAE(dadosEmpresa.cnae_principal.codigo);
          
          // Preenche automaticamente os campos do formulário
          setEmpresaForm(prev => ({
            ...prev,
            nome: dadosEmpresa.nome_fantasia || dadosEmpresa.razao_social,
            razao_social: dadosEmpresa.razao_social,
            endereco: formatarEndereco(dadosEmpresa.endereco),
            telefone: dadosEmpresa.telefone || '',
            email: dadosEmpresa.email || '',
            grau_risco: grauRisco.toString(),
            cnae_principal: dadosEmpresa.cnae_principal.codigo,
            cnae_descricao: dadosEmpresa.cnae_principal.descricao
          }));
          
          // Mostra informações do CNAE encontrado
          console.log('🏢 Empresa encontrada:', {
            razao_social: dadosEmpresa.razao_social,
            cnae: dadosEmpresa.cnae_principal.codigo,
            descricao_cnae: dadosEmpresa.cnae_principal.descricao,
            grau_risco: grauRisco
          });
        } else {
          // Mostra mensagem se não encontrou dados
          console.warn('CNPJ não encontrado ou dados indisponíveis');
        }
      } catch (error) {
        console.error('Erro ao consultar CNPJ:', error);
        // Pode adicionar um toast ou alert aqui se desejar
      } finally {
        setConsultandoCNPJ(false);
      }
    }
  };
  const [empresaForm, setEmpresaForm] = useState({
    nome: '',
    cnpj: '',
    razao_social: '',
    endereco: '',
    telefone: '',
    email: '',
    responsavel_legal: '',
    cpf_responsavel: '',
    grau_risco: '2',
    numero_funcionarios: 0,
    data_termino_mandato: '',
    observacoes: '',
    ano_assinatura: new Date().getFullYear(),
    cnae_principal: '',
    cnae_descricao: ''
  });

  // Carregar dados
  const carregarDados = async () => {
    try {
      setLoading(true);
      
      // Carregar empresas do usuário
      const { data: empresasData } = await supabase
        .from('cp_usuario_empresas')
        .select(`
          empresa:cp_empresas(*)
        `)
        .eq('usuario_id', usuarioId)
        .eq('ativo', true);

      const empresasDoUsuario = empresasData?.map(item => item.empresa).filter(Boolean) as unknown as Empresa[] || [];
      setEmpresas(empresasDoUsuario);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, [usuarioId, tipoUsuario]);

  // Verificar se pode adicionar mais empresas
  const podeAdicionarEmpresa = () => {
    if (!parametros) return false;
    if (parametros.max_empresas === -1) return true; // Ilimitado
    return empresas.length < parametros.max_empresas;
  };

  // Salvar empresa
  const salvarEmpresa = async () => {
    try {
      console.log('🔍 Iniciando salvamento de empresa...');
      console.log('🔍 Usuario ID:', usuarioId);
      console.log('🔍 Tipo Usuario:', tipoUsuario);
      
      // Validar limite de funcionários
      if (!podeTerFuncionarios(empresaForm.numero_funcionarios)) {
        const limite = getLimiteFuncionarios();
        alert(`❌ Limite de funcionários excedido!\n\nSeu plano ${tipoUsuario} permite até ${limite} funcionários.\n\nFuncionários informados: ${empresaForm.numero_funcionarios}`);
        return;
      }

      // Verificar se empresa já existe (apenas para novas empresas)
      if (!editingEmpresa && empresaForm.cnpj) {
        console.log('🔍 Verificando se empresa já existe...');
        console.log('🔍 CNPJ a verificar:', empresaForm.cnpj);
        console.log('🔍 editingEmpresa:', editingEmpresa);
        
        // Usar o novo serviço de validação
        const validacao = await validarPermissaoCadastroEmpresa(
          empresaForm.cnpj,
          usuarioId,
          tipoUsuario
        );

        if (!validacao.podeCadastrar) {
          console.log('❌ Validação falhou:', validacao.mensagem);
          showError(
            'Empresa já cadastrada',
            validacao.mensagem || 'Esta empresa já está cadastrada no sistema',
            'Verifique se você não está tentando cadastrar a mesma empresa ou entre em contato com o administrador.'
          );
          return;
        }
        
        console.log('✅ Empresa não existe, pode prosseguir');
      } else {
        console.log('⚠️ Validação de empresa duplicada pulada:', {
          editingEmpresa: !!editingEmpresa,
          hasCnpj: !!empresaForm.cnpj
        });
      }
      
      // Verificar usuário autenticado
      const { data: { user: authUser } } = await supabase.auth.getUser();
      console.log('🔍 Auth User:', authUser?.id);
      
      // Preparar dados para inserção/atualização
      const dadosEmpresa = {
        ...empresaForm,
        data_termino_mandato: empresaForm.data_termino_mandato || null,
        cnpj: empresaForm.cnpj || null,
        razao_social: empresaForm.razao_social || null,
        endereco: empresaForm.endereco || null,
        telefone: empresaForm.telefone || null,
        email: empresaForm.email || null,
        responsavel_legal: empresaForm.responsavel_legal || null,
        cpf_responsavel: empresaForm.cpf_responsavel || null,
        observacoes: empresaForm.observacoes || null,
        cnae_principal: empresaForm.cnae_principal || null,
        cnae_descricao: empresaForm.cnae_descricao || null,
        status: 'ATIVA',
        created_by: usuarioId,
        proprietario_id: usuarioId,
        ano_assinatura: empresaForm.ano_assinatura
      };

      console.log('🔍 Dados da empresa:', dadosEmpresa);

      let empresaId: string;

      if (editingEmpresa) {
        console.log('🔍 Editando empresa existente...');
        
        // Atualizar tipo de comissão baseado no CNAE usando getTipoComissao
        const comissaoInfo = await getComissaoVisualInfo(empresaForm.cnae_principal, null, empresaForm.nome);
        const tipoComissao = comissaoInfo.tipo;
        
        const { error } = await supabase
          .from('cp_empresas')
          .update({
            ...dadosEmpresa,
            tipo_comissao: tipoComissao
          })
          .eq('id', editingEmpresa.id);
        
        if (error) throw error;
        empresaId = editingEmpresa.id;
        
        console.log(`✅ Empresa atualizada. CNAE: ${empresaForm.cnae_principal} -> Tipo: ${tipoComissao} (${comissaoInfo.norma})`);
      } else {
        console.log('🔍 Criando nova empresa...');
        
        // Detectar tipo de comissão baseado no CNAE usando getTipoComissao
        const comissaoInfo = await getComissaoVisualInfo(empresaForm.cnae_principal, null, empresaForm.nome);
        const tipoComissao = comissaoInfo.tipo;
        console.log(`🔍 CNAE: ${empresaForm.cnae_principal} -> Tipo: ${tipoComissao} (${comissaoInfo.norma})`);
        
        const { data, error } = await supabase
          .from('cp_empresas')
          .insert([{
            ...dadosEmpresa,
            tipo_comissao: tipoComissao
          }])
          .select()
          .single();
        
        console.log('🔍 Resultado insert empresa:', { data, error });
        
        if (error) {
          console.error('❌ Erro detalhado:', error);
          throw error;
        }
        empresaId = data.id;
        console.log('✅ Empresa criada com ID:', empresaId);

        // Criar relacionamento usuário-empresa
        console.log('🔍 Criando relacionamento usuário-empresa...');
        const { error: relError } = await supabase
          .from('cp_usuario_empresas')
          .insert([{
            usuario_id: usuarioId,
            empresa_id: empresaId,
            tipo_acesso: 'GESTOR',
            ativo: true
          }]);
        
        console.log('🔍 Resultado insert relacionamento:', { relError });
        
        if (relError) {
          console.error('❌ Erro no relacionamento:', relError);
          throw relError;
        }
        console.log('✅ Relacionamento criado com sucesso');
      }
      
      setShowModal(false);
      setEditingEmpresa(null);
      carregarDados();
      onEmpresaChange?.();
    } catch (error) {
      console.error('❌ Erro ao salvar empresa:', error);
      alert(`Erro ao salvar empresa: ${error}`);
    }
  };

  // Deletar empresa
  const deletarEmpresa = async (empresaId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta empresa?')) return;
    
    try {
      // Desativar a empresa no banco de dados
      const { error: empresaError } = await supabase
        .from('cp_empresas')
        .update({ status: 'INATIVA' })
        .eq('id', empresaId);
      
      if (empresaError) {
        console.error('❌ Erro ao desativar empresa:', empresaError);
        throw empresaError;
      }
      console.log('✅ Empresa desativada com sucesso');

      // Desativar relacionamento usuário-empresa
      const { error: relError } = await supabase
        .from('cp_usuario_empresas')
        .update({ ativo: false })
        .eq('usuario_id', usuarioId)
        .eq('empresa_id', empresaId);
      
      if (relError) {
        console.error('❌ Erro ao desativar relacionamento:', relError);
        throw relError;
      }
      console.log('✅ Relacionamento desativado com sucesso');
      
      carregarDados();
      onEmpresaChange?.();
    } catch (error) {
      console.error('Erro ao deletar empresa:', error);
      alert('Erro ao deletar empresa');
    }
  };

  // Abrir modal
  const abrirModal = (empresa?: Empresa) => {
    setEditingEmpresa(empresa || null);
    
    if (empresa) {
        setEmpresaForm({
          nome: empresa.nome || '',
          cnpj: empresa.cnpj || '',
          razao_social: empresa.razao_social || '',
          endereco: '',
          telefone: '',
          email: '',
          responsavel_legal: '',
          cpf_responsavel: '',
          grau_risco: '2',
          numero_funcionarios: empresa.numero_funcionarios || 0,
          data_termino_mandato: '',
          observacoes: '',
          ano_assinatura: new Date().getFullYear(),
          cnae_principal: (empresa as any).cnae_principal || '',
          cnae_descricao: (empresa as any).cnae_descricao || ''
        });
    } else {
      setEmpresaForm({
        nome: '', cnpj: '', razao_social: '', endereco: '', telefone: '', email: '',
        responsavel_legal: '', cpf_responsavel: '', grau_risco: '2', numero_funcionarios: 0,
        data_termino_mandato: '', observacoes: '', ano_assinatura: new Date().getFullYear(),
        cnae_principal: '', cnae_descricao: ''
      });
    }
    
    setShowModal(true);
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      'ATIVA': 'bg-green-100 text-green-800',
      'INATIVA': 'bg-gray-100 text-gray-800',
      'SUSPENSA': 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-600">Carregando empresas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            Minhas Empresas
          </h2>
          <p className="text-gray-600 mt-1">
            {parametros && (
              <>
                {empresas.length} de {parametros.max_empresas === -1 ? '∞' : parametros.max_empresas} empresas
                {parametros.max_empresas !== -1 && (
                  <span className="ml-2 text-sm">
                    ({parametros.max_empresas - empresas.length} restantes)
                  </span>
                )}
              </>
            )}
          </p>
        </div>
        <button
          onClick={() => abrirModal()}
          disabled={!podeAdicionarEmpresa()}
          className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
            podeAdicionarEmpresa()
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          <Plus className="w-4 h-4" />
          Nova Empresa
        </button>
      </div>

      {!podeAdicionarEmpresa() && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <p className="text-yellow-800">
              Você atingiu o limite máximo de empresas para seu perfil ({parametros?.max_empresas}).
              Entre em contato com o administrador para aumentar seu limite.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {empresas.length === 0 ? (
          <div className="text-center py-8">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhuma empresa cadastrada</h3>
            <p className="text-gray-600 mb-4">Comece adicionando sua primeira empresa.</p>
            <button
              onClick={() => abrirModal()}
              disabled={!podeAdicionarEmpresa()}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 mx-auto ${
                podeAdicionarEmpresa()
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Plus className="w-4 h-4" />
              Adicionar Primeira Empresa
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Empresa</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CNPJ</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CNAE</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grau Risco</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Funcionários</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dimensionamento</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {empresas.map((empresa) => {
                  const dimensionamentoCIPA = calcularDimensionamentoCIPA(empresa.grau_risco, empresa.numero_funcionarios);
                  
                  // Usar tipo_comissao do banco diretamente
                  const tipoComissao = empresa.tipo_comissao || 'CIPA';
                  const isMining = tipoComissao === 'CIPAMIN';
                  const isRural = tipoComissao === 'CIPATR';
                  
                  return (
                    <tr key={empresa.id} className={
                      isMining ? 'bg-orange-50' : 
                      isRural ? 'bg-green-50' : 
                      ''
                    }>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {isMining && <HardHat className="h-4 w-4 text-orange-600" />}
                          {isRural && <Sprout className="h-4 w-4 text-green-600" />}
                          <div>
                            <div className="text-sm font-medium text-gray-900">{empresa.nome}</div>
                            <div className="text-sm text-gray-500">{empresa.razao_social}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{empresa.cnpj}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {(empresa as any).cnae_principal ? formatarCNAE((empresa as any).cnae_principal) : '-'}
                          </div>
                          {(empresa as any).cnae_descricao && (
                            <div className="text-xs text-gray-500 max-w-xs truncate" title={(empresa as any).cnae_descricao}>
                              {(empresa as any).cnae_descricao}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            isMining ? 'bg-orange-100 text-orange-800' :
                            isRural ? 'bg-green-100 text-green-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {tipoComissao}
                          </span>
                          <span className="text-xs text-gray-500">
                            {isMining ? 'NR-22' : isRural ? 'NR-31' : 'NR-05'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          empresa.grau_risco === 1 ? 'bg-green-100 text-green-800' :
                          empresa.grau_risco === 2 ? 'bg-yellow-100 text-yellow-800' :
                          empresa.grau_risco === 3 ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          Grau {empresa.grau_risco}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{empresa.numero_funcionarios}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="font-medium">{dimensionamentoCIPA.total} membros</div>
                          <div className="text-xs text-gray-500">
                            {dimensionamentoCIPA.efetivos} efetivos, {dimensionamentoCIPA.suplentes} suplentes
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(empresa.status)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => abrirModal(empresa)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deletarEmpresa(empresa.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">
              {editingEmpresa ? 'Editar Empresa' : 'Nova Empresa'}
            </h3>
            
            <div className="space-y-4">
              {/* CNPJ em primeiro lugar */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CNPJ *</label>
                <div className="relative">
                  <input
                    type="text"
                    value={empresaForm.cnpj}
                    onChange={(e) => {
                      const valorMascarado = aplicarMascaraCNPJ(e.target.value);
                      setEmpresaForm({...empresaForm, cnpj: valorMascarado});
                      
                      // Consulta automática quando CNPJ estiver completo
                      if (valorMascarado.replace(/\D/g, '').length === 14) {
                        consultarCNPJAutomatico(valorMascarado);
                      }
                    }}
                    placeholder="00.000.000/0000-00"
                    maxLength={18}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  {consultandoCNPJ && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    </div>
                  )}
                  {!consultandoCNPJ && empresaForm.cnpj.replace(/\D/g, '').length === 14 && (
                    <button
                      type="button"
                      onClick={() => consultarCNPJAutomatico(empresaForm.cnpj)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-600 hover:text-blue-800"
                      title="Consultar dados da empresa"
                    >
                      <Search className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Digite o CNPJ completo para buscar os dados automaticamente
                </p>
              </div>

              {/* Nome da empresa */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome da Empresa *</label>
                <input
                  type="text"
                  value={empresaForm.nome}
                  onChange={(e) => setEmpresaForm({...empresaForm, nome: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Razão Social</label>
                <input
                  type="text"
                  value={empresaForm.razao_social}
                  onChange={(e) => setEmpresaForm({...empresaForm, razao_social: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* CNAE - preenchido automaticamente */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CNAE Principal</label>
                  <input
                    type="text"
                    value={empresaForm.cnae_principal || ''}
                    onChange={(e) => setEmpresaForm({...empresaForm, cnae_principal: e.target.value})}
                    placeholder="Ex: 6201-5/00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                    readOnly
                  />
                  <p className="text-xs text-gray-500 mt-1">Preenchido automaticamente</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição CNAE</label>
                  <input
                    type="text"
                    value={empresaForm.cnae_descricao || ''}
                    onChange={(e) => setEmpresaForm({...empresaForm, cnae_descricao: e.target.value})}
                    placeholder="Ex: Desenvolvimento de programas de computador sob encomenda"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                    readOnly
                  />
                  <p className="text-xs text-gray-500 mt-1">Preenchido automaticamente</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={empresaForm.email}
                    onChange={(e) => setEmpresaForm({...empresaForm, email: e.target.value})}
                    placeholder="empresa@exemplo.com"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                      empresaForm.email && !validarEmail(empresaForm.email)
                        ? 'border-red-300 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                    }`}
                  />
                  {empresaForm.email && !validarEmail(empresaForm.email) && (
                    <p className="text-red-500 text-xs mt-1">Email inválido</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                  <input
                    type="text"
                    value={empresaForm.telefone}
                    onChange={(e) => {
                      const valorMascarado = aplicarMascaraTelefone(e.target.value);
                      setEmpresaForm({...empresaForm, telefone: valorMascarado});
                    }}
                    placeholder="(00) 0000-0000"
                    maxLength={15}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grau de Risco</label>
                  <select
                    value={empresaForm.grau_risco}
                    onChange={(e) => setEmpresaForm({...empresaForm, grau_risco: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="1">1 - Baixo</option>
                    <option value="2">2 - Médio</option>
                    <option value="3">3 - Alto</option>
                    <option value="4">4 - Muito Alto</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Grau de risco baseado no CNAE da empresa (NR-04)
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Número de Funcionários *</label>
                  <input
                    type="number"
                    value={empresaForm.numero_funcionarios}
                    onChange={(e) => setEmpresaForm({...empresaForm, numero_funcionarios: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    min="1"
                    max={parametros?.max_funcionarios === -1 ? undefined : parametros?.max_funcionarios}
                  />
                  {parametros && (
                    <p className="text-xs text-gray-500 mt-1">
                      Limite do plano {tipoUsuario}: {getLimiteFuncionarios()} funcionários
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={salvarEmpresa}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {editingEmpresa ? 'Atualizar' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Notificação */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={hideNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        details={notification.details}
      />
    </div>
  );
};

export default EmpresasManager;
